/**
 * 航路自动绑定口径（旧版加航段、导入、批量改机场、点「刷新基础数据」共用）。
 *
 * 顺序与后端 `FlightRouteService.get_best_match_route` 同一条：
 * 1. 启用中、且这架飞机能用的默认航路；
 * 2. 没有可用默认 → 启用中、适配这架飞机的航路（适配名单写了当前飞机）；
 * 3. 还没有 → 启用中、这架飞机能用的其他航路（适配名单为空表示谁都能用）；
 * 同一优先级里有多条时按航路名称升序取第一条；一条能用的都没有时返回 null，
 * 不退回绑定「只适配别的飞机」的默认航路，也不直接拿列表第一条。
 */

export function normalizeAircraftRegistration(value) {
    return String(value ?? "").trim().toUpperCase();
}

/**
 * 适配飞机名单可能是数组、逗号分隔字符串或单值；统一成字符串数组。
 */
function compatibleAircraftList(route) {
    const value = route?.compatible_aircraft;
    if (value === undefined || value === null || value === "") {
        return [];
    }
    if (Array.isArray(value)) {
        return value
            .map((item) => (item === undefined || item === null ? "" : String(item).trim()))
            .filter(Boolean);
    }
    if (typeof value === "string") {
        return value.split(/[,;\n]/).map((item) => item.trim()).filter(Boolean);
    }
    return [String(value).trim()].filter(Boolean);
}

/**
 * 这架飞机能不能用这条航路：名单为空或未填表示能用；名单非空必须包含当前飞机。
 * 没有飞机可判断时不认为写了名单的航路可用。
 */
export function routeAllowsAircraft(route, aircraftReg) {
    const compatibleAircraft = compatibleAircraftList(route);
    if (compatibleAircraft.length === 0) {
        return true;
    }
    const normalizedAircraftReg = normalizeAircraftRegistration(aircraftReg);
    if (!normalizedAircraftReg) {
        return false;
    }
    return compatibleAircraft.some((item) => normalizeAircraftRegistration(item) === normalizedAircraftReg);
}

export function isRouteAircraftSpecific(route) {
    return compatibleAircraftList(route).length > 0;
}

function isActiveRoute(route) {
    return route?.is_active !== false;
}

function sortByRouteName(routes) {
    return [...routes].sort((left, right) => {
        const nameCompare = String(left?.route_name || "").localeCompare(String(right?.route_name || ""));
        if (nameCompare !== 0) {
            return nameCompare;
        }
        return Number(left?.id || 0) - Number(right?.id || 0);
    });
}

/**
 * 该起降上「这架飞机能用的默认航路」；没有就返回 null（视为没默认）。
 * 点刷新基础数据只用这一步：没有可用默认时不改绑，不退到飞机专用。
 */
export function pickUsableDefaultRoute(routes, aircraftReg) {
    const usableDefaultRoutes = sortByRouteName(routes || []).filter(
        (route) => isActiveRoute(route) && route?.is_default && routeAllowsAircraft(route, aircraftReg),
    );
    return usableDefaultRoutes[0] || null;
}

/**
 * 系统自动绑航路时该绑哪一条；一条这架飞机能用的都没有时返回 null。
 */
export function pickBestMatchRoute(routes, aircraftReg) {
    const usableRoutes = sortByRouteName(routes || []).filter(
        (route) => isActiveRoute(route) && routeAllowsAircraft(route, aircraftReg),
    );

    const defaultRoute = usableRoutes.find((route) => route?.is_default);
    if (defaultRoute) {
        return defaultRoute;
    }

    const specificRoute = usableRoutes.find((route) => isRouteAircraftSpecific(route));
    if (specificRoute) {
        return specificRoute;
    }

    return usableRoutes[0] || null;
}
